/**
 * Metrics API - Processing metrics and QA observability
 */

import { getAPIClient } from './client';

export interface ProcessingMetric {
  id: string;
  job_id: string;
  artifact_id?: string;
  project_id: string;
  stage: 'parse' | 'chunk' | 'embed' | 'index' | 'entity_extraction';
  stage_duration_ms: number;
  total_duration_ms?: number;
  faithfulness_score?: number;
  hallucination_rate?: number;
  recall_score?: number;
  precision_score?: number;
  items_processed: number;
  items_total?: number;
  items_failed: number;
  chunks_created: number;
  avg_chunk_size_chars?: number;
  embedding_latency_ms?: number;
  embedding_tokens_used?: number;
  entities_extracted: number;
  entity_types?: Record<string, number>;
  avg_entity_confidence?: number;
  errors?: any[];
  warnings?: any[];
  metadata?: Record<string, any>;
  created_at: string;
}

export interface QADashboardData {
  summary: {
    total_jobs: number;
    total_artifacts: number;
    total_chunks: number;
    total_entities: number;
    avg_faithfulness: number;
    avg_hallucination_rate: number;
    avg_recall: number;
    avg_precision: number;
  };
  latency_breakdown: {
    stage: string;
    avg_duration_ms: number;
    p50_duration_ms: number;
    p95_duration_ms: number;
    p99_duration_ms: number;
  }[];
  quality_metrics: {
    date: string;
    faithfulness: number;
    hallucination_rate: number;
    recall: number;
    precision: number;
  }[];
  stage_metrics: {
    stage: string;
    total_processed: number;
    total_failed: number;
    success_rate: number;
    avg_duration_ms: number;
  }[];
  entity_extraction_stats: {
    entity_type: string;
    count: number;
    avg_confidence: number;
  }[];
}

export interface ProjectMetricsSummary {
  project_id: string;
  total_jobs: number;
  total_artifacts_processed: number;
  total_chunks_created: number;
  total_entities_extracted: number;
  avg_processing_time_ms: number;
  quality_scores: {
    avg_faithfulness: number;
    avg_hallucination_rate: number;
    avg_recall: number;
    avg_precision: number;
  };
  recent_metrics: ProcessingMetric[];
}

// Get processing metrics with filters
export async function getProcessingMetrics(
  projectId: string,
  filters?: {
    job_id?: string;
    artifact_id?: string;
    stage?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }
): Promise<ProcessingMetric[]> {
  const client = getAPIClient();
  return client.get<ProcessingMetric[]>('/metrics/processing', {
    params: {
      project_id: projectId,
      ...filters,
    },
  });
}

// Get QA dashboard data
export async function getQADashboard(
  projectId: string,
  days: number = 30
): Promise<QADashboardData> {
  const client = getAPIClient();
  return client.get<QADashboardData>('/metrics/qa-dashboard', {
    params: {
      project_id: projectId,
      days,
    },
  });
}

// Get metrics for a specific job
export async function getJobMetrics(jobId: string): Promise<ProcessingMetric[]> {
  const client = getAPIClient();
  return client.get<ProcessingMetric[]>(`/metrics/jobs/${jobId}`);
}

// Get project metrics summary
export async function getProjectMetricsSummary(
  projectId: string
): Promise<ProjectMetricsSummary> {
  const client = getAPIClient();
  return client.get<ProjectMetricsSummary>(`/metrics/projects/${projectId}/summary`);
}











